import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';
import { useAuth } from './AuthContext';

const IntelligenceJobsContext = createContext(null);

function mergeJobs(existingJobs, incomingJobs) {
  const byId = new Map((existingJobs || []).map((job) => [job.id, job]));
  for (const job of incomingJobs || []) {
    if (!job?.id) continue;
    byId.set(job.id, {
      ...(byId.get(job.id) || {}),
      ...job,
    });
  }

  return [...byId.values()].sort((a, b) => new Date(b.startedAt || 0) - new Date(a.startedAt || 0));
}

export function IntelligenceJobsProvider({ children }) {
  const queryClient = useQueryClient();
  const { isAuthenticated } = useAuth();
  const [panelOpen, setPanelOpen] = useState(false);
  const previousStatusesRef = useRef(new Map());

  // Track tab visibility so we don't poll while the tab is backgrounded.
  const [isVisible, setIsVisible] = useState(() =>
    typeof document === 'undefined' ? true : document.visibilityState !== 'hidden'
  );
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const onChange = () => setIsVisible(document.visibilityState !== 'hidden');
    document.addEventListener('visibilitychange', onChange);
    return () => document.removeEventListener('visibilitychange', onChange);
  }, []);

  const jobsQuery = useQuery({
    queryKey: ['account-intelligence-jobs'],
    queryFn: async () => {
      const response = await api.get('/api/accounts/intelligence/jobs?limit=25');
      return response.data?.jobs || [];
    },
    enabled: isAuthenticated, // don't fire while auth is broken/loading
    staleTime: 5_000,
    // Poll fast (3s) only when something is actually running and the tab is
    // visible. Idle = no polling at all; user actions (upsertJob/refreshJobs)
    // trigger the next refetch as needed.
    refetchInterval: (query) => {
      if (!isVisible) return false;
      const data = query?.state?.data || [];
      const hasActive = data.some((job) => job?.status === 'Running');
      return hasActive ? 3_000 : false;
    },
    refetchOnWindowFocus: false,
    retry: 0, // don't pile retries on auth failures
  });

  const jobs = jobsQuery.data || [];

  useEffect(() => {
    const previousStatuses = previousStatusesRef.current;
    for (const job of jobs) {
      const previousStatus = previousStatuses.get(job.id);
      if (previousStatus === 'Running' && job.status === 'Completed' && job.accountId) {
        queryClient.invalidateQueries({ queryKey: ['account', job.accountId] });
        queryClient.invalidateQueries({ queryKey: ['account-context', job.accountId] });
        queryClient.invalidateQueries({ queryKey: ['account-timeline', job.accountId] });
      }
      previousStatuses.set(job.id, job.status);
    }
  }, [jobs, queryClient]);

  const upsertJob = useCallback((job) => {
    if (!job?.id) return;
    queryClient.setQueryData(['account-intelligence-jobs'], (existing) => mergeJobs(existing || [], [job]));
  }, [queryClient]);

  const refreshJobs = useCallback(async () => {
    await jobsQuery.refetch();
  }, [jobsQuery]);

  const activeJobs = useMemo(
    () => jobs.filter((job) => job.status === 'Running'),
    [jobs]
  );

  const getJobForAccount = useCallback((accountId) => {
    if (!accountId) return null;
    return jobs.find((job) => job.accountId === accountId && job.status === 'Running')
      || jobs.find((job) => job.accountId === accountId);
  }, [jobs]);

  const value = useMemo(() => ({
    jobs,
    activeJobs,
    activeJobCount: activeJobs.length,
    panelOpen,
    setPanelOpen,
    upsertJob,
    getJobForAccount,
    refreshJobs,
  }), [jobs, activeJobs, panelOpen, upsertJob, getJobForAccount, refreshJobs]);

  return (
    <IntelligenceJobsContext.Provider value={value}>
      {children}
    </IntelligenceJobsContext.Provider>
  );
}

export function useIntelligenceJobs() {
  const context = useContext(IntelligenceJobsContext);
  if (!context) {
    throw new Error('useIntelligenceJobs must be used within an IntelligenceJobsProvider');
  }
  return context;
}

